import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import LanguageIcon from "@mui/icons-material/Language";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import { Link } from "react-router-dom";
import apiClient from "../../services/apiClient";
import SEO from "../../components/SEO";
import { getPriorityLabel } from "../constants/prospectLabels";
import DataGridFr from "../components/DataGridFr";

// ─── CONSTANTES ──────────────────────────────────────────
const businessTypes = [
  { value: "restaurant", label: "Restaurants" },
  { value: "hotel", label: "Hôtels" },
  { value: "avocat", label: "Avocats" },
  { value: "coiffeur", label: "Coiffeurs" },
  { value: "dentiste", label: "Dentistes" },
  { value: "plombier", label: "Plombiers" },
  { value: "electricien", label: "Electriciens" },
  { value: "garage", label: "Garages" },
  { value: "pharmacie", label: "Pharmacies" },
  { value: "boulangerie", label: "Boulangeries" },
  { value: "medecin", label: "Médecins" },
  { value: "fleuriste", label: "Fleuristes" },
  { value: "opticien", label: "Opticiens" },
  { value: "veterinaire", label: "Vétérinaires" },
  { value: "agence_immobiliere", label: "Agences immobilières" },
];

const sourcePriorities = [
  { value: "google", label: "Google d'abord" },
  { value: "pappers", label: "Pappers d'abord" },
  { value: "pagesjaunes", label: "Pages Jaunes d'abord" },
];

const targetCMSOptions = [
  { value: "Wix", label: "Wix" },
  { value: "Jimdo", label: "Jimdo" },
  { value: "Weebly", label: "Weebly" },
  { value: "Squarespace", label: "Squarespace" },
  { value: "Duda", label: "Duda" },
  { value: "WordPress", label: "WordPress" },
  { value: "Webflow", label: "Webflow" },
];

// ─── COMPOSANTS UTILITAIRES ──────────────────────────────
function ScoreChip({ score }) {
  const color = score >= 60 ? "success" : score >= 35 ? "warning" : "default";
  return <Chip size="small" label={`${score ?? "-"}/100`} color={color} />;
}

function ReasonsTooltip({ positives = [], missing = [] }) {
  return (
    <Tooltip arrow placement="left" title={
      <Box sx={{ maxWidth: 320, p: 0.5 }}>
        {positives.length > 0 && (
          <>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "success.main" }}>Forces</Typography>
            {positives.map((r, i) => <Typography key={i} variant="caption" display="block">+ {r}</Typography>)}
          </>
        )}
        {missing.length > 0 && (
          <>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "error.main", mt: 0.5 }}>Manques</Typography>
            {missing.map((r, i) => <Typography key={i} variant="caption" display="block">- {r}</Typography>)}
          </>
        )}
        {positives.length === 0 && missing.length === 0 && <Typography variant="caption">Aucune donnée</Typography>}
      </Box>
    }>
      <IconButton size="small" aria-label="Détails du score"><InfoOutlinedIcon fontSize="small" /></IconButton>
    </Tooltip>
  );
}

function SourceSummaryChips({ summary }) {
  if (!summary || Object.keys(summary).length === 0) return null;
  const labels = { google_nearby: "Google Nearby", google_text: "Google Text", google: "Google", pappers: "Pappers", pj_api: "PJ API", pj_scraping: "PJ Scraping", societe_com: "Societe.com" };
  return (
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
      {Object.entries(summary).map(([src, count]) => (
        <Chip key={src} size="small" variant="outlined" label={`${labels[src] || src}: ${count}`}
          color={count > 0 ? "primary" : "default"} />
      ))}
    </Box>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: 1.2, mb: 0.5, display: "block" }}>
      {children}
    </Typography>
  );
}

// ═══════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════
export default function AdminProspectionSearchPage() {
  const [activeTab, setActiveTab] = useState(0);

  // Recherche locale
  const [location, setLocation] = useState("");
  const [radius, setRadius] = useState(5);
  const [businessType, setBusinessType] = useState("restaurant");
  const [sourcePriority, setSourcePriority] = useState("google");
  const [includeGoogle, setIncludeGoogle] = useState(true);
  const [includePjScraping, setIncludePjScraping] = useState(false);
  const [maxResults, setMaxResults] = useState(40);
  const [minScore, setMinScore] = useState(0);
  const [filterExisting, setFilterExisting] = useState(true);

  // Détection tech
  const [techDomains, setTechDomains] = useState("");
  const [techTargetCMS, setTechTargetCMS] = useState(["Wix", "Jimdo", "Weebly", "Duda"]);

  // Import CSV
  const [csvText, setCsvText] = useState("");
  const [csvMode, setCsvMode] = useState("prospects");
  const csvFileRef = useRef(null);

  // Nouveaux domaines (crt.sh)
  const [domainKeyword, setDomainKeyword] = useState("");
  const [domainTld, setDomainTld] = useState("fr");
  const [domainDays, setDomainDays] = useState(30);

  // Options avancées
  const [includeSociete, setIncludeSociete] = useState(true);
  const [discoverWebsites, setDiscoverWebsites] = useState(false);

  // Enregistrement BDD : par défaut rien n'est importé tant que tu n'as pas coché « brouillon » ou « Ajouter au pipeline ».
  const [autoImportDraft, setAutoImportDraft] = useState(false);
  const [autoQualify, setAutoQualify] = useState(true);
  const [autoEnrich, setAutoEnrich] = useState(false);

  // Résultats (partagé)
  const [searchRows, setSearchRows] = useState([]);
  const [importedRows, setImportedRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [importedSelected, setImportedSelected] = useState([]);
  const [baseCount, setBaseCount] = useState(0);
  const [searching, setSearching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [bulkBusy, setBulkBusy] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });
  const [detailRow, setDetailRow] = useState(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [lastSourceSummary, setLastSourceSummary] = useState(null);

  const refreshBaseCount = useCallback(async () => {
    try {
      const data = await apiClient.adminProspectsList("limit=1");
      setBaseCount(data.total || 0);
    } catch (e) { console.warn("refreshBaseCount:", e.message); }
  }, []);

  useEffect(() => { refreshBaseCount(); }, [refreshBaseCount]);

  // ─── RECHERCHE LOCALE ──────────────────────────────────
  const searchLocal = async () => {
    if (!location.trim()) { setMessage({ type: "error", text: "Saisis une localisation." }); return; }
    setSearching(true); setMessage({ type: "", text: "" }); setImportedRows([]); setLastSourceSummary(null);
    try {
      const data = await apiClient.adminProspectionSearch({
        location, radius: Number(radius), businessType,
        options: {
          includeGoogle, includePjApi: true, includePjScraping,
          maxResults: Number(maxResults), minScore: Number(minScore) || 0,
          filterExisting, autoQualify, autoEnrich, sourcePriority,
          includeSociete, discoverWebsites,
        },
      });
      applySearchResults(data);
    } catch (e) { setMessage({ type: "error", text: e.message || "Recherche impossible." }); }
    finally { setSearching(false); }
  };

  // ─── DETECTION TECH ────────────────────────────────────
  const searchTech = async () => {
    const domains = techDomains.split(/[\n,;]+/).map((d) => d.trim()).filter(Boolean);
    if (domains.length === 0) { setMessage({ type: "error", text: "Saisis au moins un domaine ou URL." }); return; }
    setSearching(true); setMessage({ type: "", text: "" }); setLastSourceSummary(null);
    try {
      const data = await apiClient.adminProspectionTechScan({ domains, targetCMS: techTargetCMS });
      setSearchRows(data.results || []); setSelectedIds([]);
      const total = data.total || 0;
      const scanned = data.scanned || 0;
      setMessage({
        type: total === 0 ? "warning" : "success",
        text: total === 0 ? `${scanned} domaine(s) scanné(s), aucun ne correspond aux CMS ciblés.` : `${total} site(s) détecté(s) sur ${scanned} scanné(s) (${techTargetCMS.join(", ")}).`,
      });
    } catch (e) { setMessage({ type: "error", text: e.message }); }
    finally { setSearching(false); }
  };

  // ─── IMPORT CSV ────────────────────────────────────────
  const handleCsvFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(ev.target.result);
    reader.readAsText(file, "utf-8");
  };

  const searchCsv = async () => {
    if (!csvText.trim()) { setMessage({ type: "error", text: "Charge un fichier CSV ou colle du contenu." }); return; }
    setSearching(true); setMessage({ type: "", text: "" }); setLastSourceSummary(null);
    try {
      const data = await apiClient.adminProspectionCsvImport({ csvText, mode: csvMode, targetCMS: techTargetCMS });
      setSearchRows(data.results || []); setSelectedIds([]);
      const total = data.total || 0;
      setMessage({ type: total === 0 ? "warning" : "success", text: data.message || `${total} prospect(s) chargé(s) depuis le CSV.` });
    } catch (e) { setMessage({ type: "error", text: e.message }); }
    finally { setSearching(false); }
  };

  // ─── NOUVEAUX DOMAINES ──────────────────────────────────
  const searchNewDomains = async () => {
    if (!domainKeyword.trim()) { setMessage({ type: "error", text: "Saisis un mot-clé (ex: plombier, restaurant, coiffeur)." }); return; }
    setSearching(true); setMessage({ type: "", text: "" }); setLastSourceSummary(null);
    try {
      const data = await apiClient.adminProspectionNewDomains({ keyword: domainKeyword, tld: domainTld, days: domainDays, limit: 100 });
      setSearchRows(data.results || []); setSelectedIds([]);
      const total = data.total || 0;
      setMessage({
        type: total === 0 ? "warning" : "success",
        text: total === 0 ? `Aucun domaine récent pour "${domainKeyword}" (.${domainTld}).` : `${total} domaine(s) enregistré(s) ces ${domainDays} derniers jours.`,
      });
    } catch (e) { setMessage({ type: "error", text: e.message }); }
    finally { setSearching(false); }
  };

  // ─── RESULTATS HELPERS ─────────────────────────────────
  const applySearchResults = (data) => {
    setSearchRows(data.results || []); setSelectedIds([]); setLastSourceSummary(data.sourceSummary || null);
    const count = data.results?.length || 0;
    const filtered = data.filteredExistingCount || 0;
    const lowScore = data.filteredLowScoreCount || 0;
    const pool = data.poolSize || 0;
    const target = Number(maxResults);
    const parts = [];
    if (filtered > 0) parts.push(`${filtered} déjà en base`);
    if (lowScore > 0) parts.push(`${lowScore} sous le score min`);
    const filterMsg = parts.length > 0 ? ` (${parts.join(", ")} écartés)` : "";
    const geoMsg = data.geocodeFallback ? " ⚠ Localisation non résolue, résultats sur Paris." : "";
    const autoMsg = data.autoImported > 0
      ? ` ${data.autoImported} enregistré(s) en brouillon (filtre « Brouillons » dans le pipeline pour les valider).`
      : "";
    const enrichMsg = autoEnrich && data.autoImported > 0 ? " Enrichissement en arrière-plan." : "";
    const shortageMsg = (minScore > 0 && count < target && pool > 0)
      ? ` ⚠ ${count}/${target} : pool de ${pool} insuffisant.`
      : "";
    setMessage({
      type: count === 0 ? "warning" : (count < target && minScore > 0) ? "info" : "success",
      text: count === 0
        ? `Aucun résultat${filterMsg}.${geoMsg}`
        : `${count} résultat(s)${filterMsg}.${autoMsg}${enrichMsg}${shortageMsg}${geoMsg}`,
    });
    if (data.autoImported > 0) refreshBaseCount();
  };

  const removeSelected = () => {
    const set = new Set(selectedIds);
    setSearchRows((prev) => prev.filter((r) => !set.has(r.id)));
    setSelectedIds([]);
  };

  const importSelected = async () => {
    const set = new Set(selectedIds);
    const items = searchRows.filter((r) => set.has(r.id)).map((r) => ({
      ...r,
      preview_score: r.preview_score, preview_priority_level: r.preview_priority_level,
      preview_approachability: r.preview_approachability,
    }));
    if (items.length === 0) return;
    setImporting(true); setMessage({ type: "", text: "" });
    try {
      const data = await apiClient.adminProspectsImportItems({ sourceName: "admin_search", autoQualify, autoEnrich, items });
      await refreshBaseCount();
      setImportedRows(data.rows || []);
      setSearchRows((prev) => prev.filter((r) => !set.has(r.id)));
      setSelectedIds([]);
      setMessage({ type: "success", text: `${data.imported || 0} prospect(s) ajouté(s).${autoEnrich ? " Enrichissement en arrière-plan." : ""}` });
    } catch (e) { setMessage({ type: "error", text: e.message || "Import impossible." }); }
    finally { setImporting(false); }
  };

  const runBulkOnImported = async (action, options = {}) => {
    if (importedRows.length === 0) return;
    const ids = importedSelected.length > 0 ? importedSelected : importedRows.map((r) => r.id);
    setBulkBusy(action); setMessage({ type: "", text: "" });
    try {
      const report = await apiClient.adminProspectsBulkAction({ ids, action, options });
      setMessage({ type: "success", text: `"${action}" : ${report.success}/${report.total} réussi(s).` });
    } catch (e) { setMessage({ type: "error", text: e.message }); }
    finally { setBulkBusy(""); }
  };

  const runBulkOnSelected = async (action, options = {}) => {
    const set = new Set(selectedIds);
    const dbIds = searchRows.filter((r) => set.has(r.id)).map((r) => r.db_id).filter(Boolean);
    if (dbIds.length === 0) {
      setMessage({ type: "warning", text: "Sélection non importée. Activez 'Auto-qualifier' ou importez d'abord." });
      return;
    }
    setBulkBusy(action); setMessage({ type: "", text: "" });
    try {
      const report = await apiClient.adminProspectsBulkAction({ ids: dbIds, action, options });
      setMessage({ type: "success", text: `${report.success}/${report.total} traité(s).` });
    } catch (e) { setMessage({ type: "error", text: e.message }); }
    finally { setBulkBusy(""); }
  };

  const hasSelection = selectedIds.length > 0;
  const selectedHaveDbIds = searchRows.some((r) => selectedIds.includes(r.id) && r.db_id);

  const handleLaunch = () => {
    if (activeTab === 0) searchLocal();
    else if (activeTab === 1) searchTech();
    else if (activeTab === 2) searchCsv();
    else if (activeTab === 3) searchNewDomains();
  };

  // ─── COLONNES RESULTATS ────────────────────────────────
  const searchColumns = useMemo(() => [
    {
      field: "name",
      headerName: "Entreprise",
      flex: 1.5,
      minWidth: 180,
      renderCell: (p) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>{p.value}</Typography>
          {p.row.cms && <Chip size="small" label={p.row.cms} color="warning" sx={{ mt: 0.25 }} />}
        </Box>
      ),
    },
    { field: "address", headerName: "Adresse", flex: 1, minWidth: 140 },
    { field: "phone", headerName: "Tél", width: 120 },
    {
      field: "website",
      headerName: "Site",
      flex: 0.8,
      minWidth: 130,
      renderCell: (p) => p.value ? (
        <Typography variant="caption" component="a" href={p.value} target="_blank" rel="noopener" sx={{ color: "primary.main" }}
          onClick={(e) => e.stopPropagation()}>
          {(() => { try { return new URL(p.value).hostname; } catch { return p.value; } })()}
        </Typography>
      ) : <Typography variant="caption" color="error.main">Aucun</Typography>,
    },
    {
      field: "source",
      headerName: "Source",
      width: 120,
      type: "singleSelect",
      valueOptions: ["google_places", "pappers", "pappers_scraping", "pages_jaunes", "pj_scraping", "societe_com", "crt_sh", "csv", "python_scraping"],
      renderCell: (p) => <Chip size="small" label={p.value || "-"} variant="outlined" />,
    },
    {
      field: "preview_score",
      headerName: "Score",
      width: 90,
      type: "number",
      align: "center",
      headerAlign: "center",
      valueGetter: (value, row) => row.db_score ?? row.preview_score ?? 0,
      renderCell: (p) => <ScoreChip score={p.value} />,
    },
    {
      field: "preview_priority_level",
      headerName: "Priorité",
      width: 110,
      renderCell: (p) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="caption">{getPriorityLabel(p.value)}</Typography>
          {p.row.tech_problems?.length > 0 && (
            <Tooltip title={p.row.tech_problems.join(" · ")} arrow>
              <Chip size="small" label={`${p.row.tech_problems.length} pb`} color="error" variant="outlined" />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: "reasons",
      headerName: "Infos",
      width: 60,
      sortable: false,
      filterable: false,
      disableExport: true,
      align: "center",
      renderCell: (p) => <ReasonsTooltip positives={p.row.preview_reasons_positive} missing={p.row.preview_reasons_missing} />,
    },
    {
      field: "actions",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      disableExport: true,
      renderCell: (p) => p.row.db_id ? (
        <Tooltip title="Ouvrir la fiche">
          <IconButton size="small" component={Link} to={`/admin/prospects/${p.row.db_id}`} onClick={(e) => e.stopPropagation()}>
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Voir le détail">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDetailRow(p.row); }}>
            <OpenInNewIcon fontSize="small" color="action" />
          </IconButton>
        </Tooltip>
      ),
    },
  ], []);

  const importedColumns = useMemo(() => [
    { field: "company_name", headerName: "Entreprise", flex: 1.5, minWidth: 180 },
    {
      field: "qualification_status",
      headerName: "Statut",
      width: 130,
      renderCell: (p) => <Chip size="small" label={p.value} variant="outlined" />,
    },
    {
      field: "global_score",
      headerName: "Score",
      width: 90,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderCell: (p) => <ScoreChip score={p.value} />,
    },
    {
      field: "website_url",
      headerName: "Site",
      flex: 1,
      minWidth: 130,
      renderCell: (p) => p.value ? (
        <Typography variant="caption" color="primary.main">{p.value}</Typography>
      ) : <Typography variant="caption" color="error.main">Aucun</Typography>,
    },
    {
      field: "open",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      disableExport: true,
      renderCell: (p) => (
        <Button component={Link} to={`/admin/prospects/${p.row.id}`} size="small">Ouvrir</Button>
      ),
    },
  ], []);

  return (
    <>
      <SEO title="Trouver des prospects | DigiBuddy" noIndex noFollow />
      <Typography variant="h5" sx={{ mb: 0.5 }}>1. Trouver des prospects</Typography>
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        Choisissez un module de découverte, configurez l'enrichissement, et lancez.
      </Typography>

      {/* ═══ MODULES DE DECOUVERTE ═══ */}
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab icon={<SearchIcon />} iconPosition="start" label="Recherche locale" />
            <Tab icon={<LanguageIcon />} iconPosition="start" label="Détection sites (CMS)" />
            <Tab icon={<UploadFileIcon />} iconPosition="start" label="Import CSV" />
            <Tab icon={<TravelExploreIcon />} iconPosition="start" label="Nouveaux domaines" />
          </Tabs>
        </Box>

        <Box sx={{ p: 2 }}>
          {activeTab === 0 && (
            <>
              <SectionLabel>Paramètres de recherche</SectionLabel>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "flex-start", mb: 1.5 }}>
                <TextField label="Localisation" size="small" value={location}
                  onChange={(e) => setLocation(e.target.value)} placeholder="Paris 75001" sx={{ minWidth: 180 }} />
                <TextField label="Rayon (km)" size="small" type="number" value={radius}
                  onChange={(e) => setRadius(e.target.value)} sx={{ width: 100 }} />
                <TextField select label="Secteur" size="small" value={businessType}
                  onChange={(e) => setBusinessType(e.target.value)} sx={{ minWidth: 160 }}>
                  {businessTypes.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
                </TextField>
                <TextField select label="Source prioritaire" size="small" value={sourcePriority}
                  onChange={(e) => setSourcePriority(e.target.value)} sx={{ minWidth: 170 }}>
                  {sourcePriorities.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                </TextField>
                <TextField label="Nb max" size="small" type="number" value={maxResults}
                  onChange={(e) => setMaxResults(Math.max(5, Math.min(200, Number(e.target.value) || 20)))}
                  sx={{ width: 90 }} inputProps={{ min: 5, max: 200 }} />
                <TextField label="Score min" size="small" type="number" value={minScore}
                  onChange={(e) => setMinScore(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                  sx={{ width: 100 }} inputProps={{ min: 0, max: 100 }}
                  helperText={minScore > 0 ? `≥ ${minScore}/100` : "Aucun"} />
              </Box>

              <SectionLabel>Sources de découverte</SectionLabel>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1 }}>
                <FormControlLabel control={<Checkbox size="small" checked={includeGoogle} onChange={(e) => setIncludeGoogle(e.target.checked)} />} label="Google Places" />
                <FormControlLabel control={<Checkbox size="small" checked disabled />} label="Pappers (API + scraping)" />
                <FormControlLabel control={<Checkbox size="small" checked disabled />} label="Pages Jaunes API" />
                <FormControlLabel control={<Checkbox size="small" checked={includeSociete} onChange={(e) => setIncludeSociete(e.target.checked)} />} label="Societe.com" />
                <FormControlLabel control={<Checkbox size="small" checked={includePjScraping} onChange={(e) => setIncludePjScraping(e.target.checked)} />} label="Scraping PJ (lent)" />
              </Box>
              <SectionLabel>Options</SectionLabel>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1 }}>
                <FormControlLabel control={<Checkbox size="small" checked={filterExisting} onChange={(e) => setFilterExisting(e.target.checked)} />} label="Exclure déjà en base" />
                <FormControlLabel control={<Checkbox size="small" checked={discoverWebsites} onChange={(e) => setDiscoverWebsites(e.target.checked)} />}
                  label={<><span>Chercher les sites web manquants</span><Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>(DuckDuckGo, +lent)</Typography></>} />
              </Box>
            </>
          )}

          {activeTab === 1 && (
            <>
              <SectionLabel>Domaines / URLs à scanner</SectionLabel>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                Colle une liste de domaines. Les sites construits avec un CMS "template" (Wix, Jimdo...) sont des prospects idéaux pour une refonte.
              </Typography>
              <TextField multiline rows={5} fullWidth size="small" value={techDomains}
                onChange={(e) => setTechDomains(e.target.value)}
                placeholder={"exemple-restaurant.fr\nmon-plombier.wixsite.com\nhttps://www.coiffure-paris.fr"}
                sx={{ mb: 1.5, fontFamily: "monospace" }} />
              <SectionLabel>CMS ciblés</SectionLabel>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                {targetCMSOptions.map((opt) => (
                  <FormControlLabel key={opt.value} control={
                    <Checkbox size="small" checked={techTargetCMS.includes(opt.value)}
                      onChange={(e) => {
                        if (e.target.checked) setTechTargetCMS((p) => [...p, opt.value]);
                        else setTechTargetCMS((p) => p.filter((v) => v !== opt.value));
                      }} />
                  } label={opt.label} />
                ))}
              </Box>
            </>
          )}

          {activeTab === 2 && (
            <>
              <SectionLabel>Fichier CSV ou contenu</SectionLabel>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                Charge un fichier CSV ou colle le contenu. Colonnes auto-détectées (nom, site_web, telephone, adresse, email...).
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1.5 }}>
                <input type="file" accept=".csv,.txt" ref={csvFileRef} style={{ display: "none" }} onChange={handleCsvFile} />
                <Button variant="outlined" size="small" startIcon={<UploadFileIcon />} onClick={() => csvFileRef.current?.click()}>Charger un fichier</Button>
                <TextField select label="Mode" size="small" value={csvMode} onChange={(e) => setCsvMode(e.target.value)} sx={{ width: 220 }}>
                  <MenuItem value="prospects">Import direct (prospects)</MenuItem>
                  <MenuItem value="tech_scan">Scanner les sites (CMS)</MenuItem>
                </TextField>
              </Box>
              <TextField multiline rows={5} fullWidth size="small" value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                placeholder={"nom;site_web;telephone;adresse\nRestaurant Le Soleil;https://lesoleil.fr;0145678901;12 rue de Paris"}
                sx={{ fontFamily: "monospace" }} />
            </>
          )}

          {activeTab === 3 && (
            <>
              <SectionLabel>Découverte de domaines récemment enregistrés</SectionLabel>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                Trouve des sites web récemment créés par mot-clé via les certificats SSL (crt.sh).
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "flex-start", mb: 1 }}>
                <TextField label="Mot-clé" size="small" value={domainKeyword}
                  onChange={(e) => setDomainKeyword(e.target.value)} placeholder="plombier, restaurant..." sx={{ minWidth: 220 }} />
                <TextField select label="TLD" size="small" value={domainTld} onChange={(e) => setDomainTld(e.target.value)} sx={{ width: 100 }}>
                  <MenuItem value="fr">.fr</MenuItem>
                  <MenuItem value="com">.com</MenuItem>
                  <MenuItem value="paris">.paris</MenuItem>
                </TextField>
                <TextField label="Derniers jours" size="small" type="number" value={domainDays}
                  onChange={(e) => setDomainDays(Math.max(1, Math.min(90, Number(e.target.value) || 30)))}
                  sx={{ width: 130 }} inputProps={{ min: 1, max: 90 }} />
              </Box>
            </>
          )}

          {/* ═══ ENREGISTREMENT & ENRICHISSEMENT (partagé) ═══ */}
          <Divider sx={{ my: 2 }} />
          <SectionLabel>Enregistrement en base</SectionLabel>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block", maxWidth: 720 }}>
            Par défaut, une recherche ne crée pas de fiches : les résultats restent dans ce tableau jusqu’à ce que tu cliques « Ajouter au pipeline ».
            Coche ci-dessous seulement si tu veux enregistrer tout de suite sans les mélanger au pipeline actif.
          </Typography>
          <FormControlLabel
            control={<Checkbox size="small" checked={autoImportDraft} onChange={(e) => setAutoImportDraft(e.target.checked)} />}
            label={<Box component="span"><strong>Brouillon</strong> — enregistrer les résultats en base (statut « à valider », visibles via le filtre Brouillons dans le pipeline)</Box>}
          />
          <SectionLabel sx={{ mt: 1 }}>À l&apos;import (brouillon auto ou bouton « Ajouter au pipeline »)</SectionLabel>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <FormControlLabel control={<Checkbox size="small" checked={autoQualify} onChange={(e) => setAutoQualify(e.target.checked)} />}
              label="Scorer / recalculer après import" />
            <FormControlLabel control={<Checkbox size="small" checked={autoEnrich} onChange={(e) => setAutoEnrich(e.target.checked)} />}
              label="Enrichir en arrière-plan (Google + Pappers + PJ + scan web)" />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Button variant="contained" size="large" onClick={handleLaunch} disabled={searching}>
              {searching ? "Recherche en cours..." : "Lancer la recherche"}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* ═══ MESSAGES ═══ */}
      {message.text && (
        <Alert severity={message.type || "info"} sx={{ mb: 2 }} onClose={() => setMessage({ type: "", text: "" })}>
          {message.text}
        </Alert>
      )}

      {lastSourceSummary && (
        <Box sx={{ mb: 1.5, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <Typography variant="caption" color="text.secondary">Sources :</Typography>
          <SourceSummaryChips summary={lastSourceSummary} />
        </Box>
      )}

      {/* ═══ BARRE D'ACTIONS ═══ */}
      {searchRows.length > 0 && (
        <Paper sx={{ p: 1.5, mb: 2 }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <Typography variant="body2">
              <b>{searchRows.length}</b> résultat(s) · <b>{selectedIds.length}</b> sélectionné(s) · Pipeline: <b>{baseCount}</b>
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button size="small" variant="contained" disabled={!hasSelection || importing} onClick={importSelected}>
              {importing ? "Import..." : `Ajouter au pipeline (${selectedIds.length})`}
            </Button>
            {selectedHaveDbIds && (
              <>
                <Button size="small" variant="outlined" disabled={!hasSelection || Boolean(bulkBusy)} onClick={() => runBulkOnSelected("recalculate")}>
                  {bulkBusy === "recalculate" ? "..." : "Rescorer"}
                </Button>
                <Button size="small" variant="outlined" disabled={!hasSelection || Boolean(bulkBusy)} onClick={() => runBulkOnSelected("enrich_all")}>
                  {bulkBusy === "enrich_all" ? "..." : "Ré-enrichir"}
                </Button>
              </>
            )}
            <Button size="small" variant="outlined" color="error" disabled={!hasSelection}
              onClick={removeSelected} startIcon={<DeleteOutlineIcon />}>
              Retirer
            </Button>
          </Box>
        </Paper>
      )}

      {/* ═══ TABLEAU DES RESULTATS ═══ */}
      {searchRows.length > 0 && (
        <Paper sx={{ width: "100%", mb: 2 }}>
          <DataGridFr
            rows={searchRows}
            columns={searchColumns}
            checkboxSelection
            rowSelectionModel={selectedIds}
            onRowSelectionModelChange={setSelectedIds}
            initialSortModel={[{ field: "preview_score", sort: "desc" }]}
            pageSize={25}
          />
        </Paper>
      )}

      {/* ═══ PROSPECTS IMPORTES ═══ */}
      {importedRows.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Prospects importés ({importedRows.length})</Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
            <Button size="small" variant="outlined" disabled={Boolean(bulkBusy)} onClick={() => runBulkOnImported("recalculate")}>
              {bulkBusy === "recalculate" ? "..." : "Scorer tout"}
            </Button>
            <Button size="small" variant="outlined" disabled={Boolean(bulkBusy)} onClick={() => runBulkOnImported("enrich", { source: "website_scan" })}>
              {bulkBusy === "enrich" ? "..." : "Scanner sites"}
            </Button>
            <Button size="small" variant="outlined" disabled={Boolean(bulkBusy)} onClick={() => runBulkOnImported("enrich_all")}>
              {bulkBusy === "enrich_all" ? "..." : "Enrichir toutes sources"}
            </Button>
            <Button size="small" variant="outlined" color="error" disabled={Boolean(bulkBusy)} onClick={() => setConfirmBulkDelete(true)}>
              Supprimer tout
            </Button>
          </Box>
          <DataGridFr
            rows={importedRows}
            columns={importedColumns}
            checkboxSelection
            rowSelectionModel={importedSelected}
            onRowSelectionModelChange={setImportedSelected}
            initialSortModel={[{ field: "global_score", sort: "desc" }]}
            pageSize={10}
            showExport={false}
            showDensity={false}
            showColumns={false}
          />
        </Paper>
      )}

      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
        <Button component={Link} to="/admin/prospects" variant="outlined" size="small">Ouvrir le pipeline</Button>
      </Box>

      {/* ═══ DIALOGS ═══ */}
      <Dialog open={confirmBulkDelete} onClose={() => setConfirmBulkDelete(false)}>
        <DialogTitle>Supprimer les prospects importés ?</DialogTitle>
        <DialogContent>
          <Typography>Les {importedRows.length} prospect(s) seront définitivement supprimés.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBulkDelete(false)}>Annuler</Button>
          <Button color="error" variant="contained" onClick={async () => {
            setConfirmBulkDelete(false);
            await runBulkOnImported("delete");
            setImportedRows([]);
          }}>Supprimer tout</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(detailRow)} onClose={() => setDetailRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{detailRow?.name}</DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {[
                ["Adresse", detailRow.address],
                ["Téléphone", detailRow.phone],
                ["Email", detailRow.email],
                ["SIREN", detailRow.siren],
                ["Effectifs", detailRow.employee_range],
              ].filter(([, v]) => v).map(([label, value]) => (
                <Box key={label}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="body2">{value}</Typography>
                </Box>
              ))}
              <Box>
                <Typography variant="caption" color="text.secondary">Site web</Typography>
                <Typography variant="body2">
                  {detailRow.website
                    ? <a href={detailRow.website} target="_blank" rel="noopener noreferrer">{detailRow.website}</a>
                    : <span style={{ color: "red" }}>Aucun site trouvé</span>}
                </Typography>
              </Box>
              {detailRow.cms && (
                <Box>
                  <Typography variant="caption" color="text.secondary">CMS détecté</Typography>
                  <Chip size="small" label={detailRow.cms} color="warning" />
                </Box>
              )}
              {detailRow.tech_problems?.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Problèmes détectés</Typography>
                  {detailRow.tech_problems.map((p, i) => (
                    <Typography key={i} variant="caption" display="block" color="error.main">• {p}</Typography>
                  ))}
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <ScoreChip score={detailRow.db_score ?? detailRow.preview_score} />
                <Typography variant="caption">{getPriorityLabel(detailRow.preview_priority_level)}</Typography>
              </Box>
              {(detailRow.preview_reasons_positive?.length > 0 || detailRow.preview_reasons_missing?.length > 0) && (
                <Box>
                  {detailRow.preview_reasons_positive?.map((r, i) => (
                    <Typography key={`p${i}`} variant="caption" display="block" color="success.main">+ {r}</Typography>
                  ))}
                  {detailRow.preview_reasons_missing?.map((r, i) => (
                    <Typography key={`m${i}`} variant="caption" display="block" color="error.main">- {r}</Typography>
                  ))}
                </Box>
              )}
              {detailRow.rating > 0 && (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Typography variant="caption" color="text.secondary">Avis</Typography>
                  <Typography variant="body2">{detailRow.rating}/5 ({detailRow.reviewCount} avis)</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailRow(null)}>Fermer</Button>
          {detailRow?.db_id && (
            <Button component={Link} to={`/admin/prospects/${detailRow.db_id}`} variant="contained">Ouvrir la fiche</Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
